import { useState, useEffect } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronRight, Server } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { INCIDENTS } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityStyle = {
  critical: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  warning: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  info: "text-blue-400 bg-blue-400/10 border-blue-400/20",
};
const statusStyle = {
  active: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  resolved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  investigating: "text-amber-400 bg-amber-400/10 border-amber-400/20",
};

function IncidentCard({ inc }) {
  const [expanded, setExpanded] = useState(false);
  const duration = inc.duration_minutes
    ? `${inc.duration_minutes}m`
    : inc.started_at ? `${Math.round((Date.now() - new Date(inc.started_at)) / 60000)}m (ongoing)` : "—";

  return (
    <div data-testid={INCIDENTS.incidentCard} className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden animate-fade-in">
      <button className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-800/30 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}>
        <div className={cn("w-1 h-8 rounded-full flex-shrink-0",
          inc.severity === "critical" ? "bg-rose-400" : inc.severity === "warning" ? "bg-amber-400" : "bg-blue-400")} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-slate-100 truncate">{inc.title}</span>
            <span data-testid={INCIDENTS.statusBadge} className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase flex-shrink-0", statusStyle[inc.status])}>
              {inc.status}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="font-mono">{inc.cluster_id}</span>
            <span>{inc.namespace}</span>
            <span className="flex items-center gap-1"><Clock size={9} />{duration}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("text-[10px] px-2 py-0.5 rounded border font-semibold uppercase flex-shrink-0", severityStyle[inc.severity])}>
            {inc.severity}
          </span>
          {expanded ? <ChevronDown size={13} className="text-slate-500" /> : <ChevronRight size={13} className="text-slate-500" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/60 space-y-4 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Affected Services</p>
              <div className="flex flex-wrap gap-1.5">
                {inc.affected_services.map((s) => (
                  <span key={s} className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono border border-slate-700">{s}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Impact</p>
              <p className="text-xs text-slate-300">{inc.impact}</p>
            </div>
          </div>

          {inc.root_cause && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Root Cause</p>
              <p className="text-xs text-slate-300 bg-slate-800/50 border border-slate-700 rounded px-3 py-2 font-mono">{inc.root_cause}</p>
            </div>
          )}

          {inc.resolution && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Resolution</p>
              <p className="text-xs text-slate-300">{inc.resolution}</p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Timeline</p>
            <div className="space-y-2 border-l border-slate-800 ml-2 pl-3">
              {inc.timeline.map((tl, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[17px] w-2 h-2 rounded-full bg-slate-700 border border-slate-600" />
                  <p className="text-[10px] text-slate-500 font-mono mb-0.5">{new Date(tl.time).toLocaleString()}</p>
                  <p className="text-xs text-slate-300">{tl.event}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/incidents`)
      .then((r) => { setIncidents(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? incidents : incidents.filter((i) => i.status === filter);
  const active = incidents.filter((i) => i.status === "active").length;
  const resolved = incidents.filter((i) => i.status === "resolved").length;

  return (
    <div data-testid={INCIDENTS.page} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Incident Investigation</h2>
          <p className="text-sm text-slate-400 mt-0.5">{active} active · {resolved} resolved · {incidents.length} total</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Active", value: active, color: "text-rose-400", icon: AlertTriangle },
          { label: "Resolved", value: resolved, color: "text-emerald-400", icon: CheckCircle },
          { label: "Total (30d)", value: incidents.length, color: "text-slate-50", icon: Server },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
              <Icon size={13} className={color} />
            </div>
            <p className={cn("text-2xl font-semibold", color)} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-1 bg-slate-900/50 border border-slate-800 rounded-md p-1 w-fit">
        {["all", "active", "resolved"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={cn("px-3 py-1.5 rounded text-xs font-medium transition-colors capitalize",
              filter === f ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "text-slate-400 hover:text-slate-200")}>
            {f} {f === "active" ? `(${active})` : f === "resolved" ? `(${resolved})` : `(${incidents.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-800/50 rounded-md animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle size={32} className="mx-auto mb-3 text-emerald-400 opacity-50" />
              <p className="text-sm">No {filter !== "all" ? filter : ""} incidents found</p>
            </div>
          )}
          {filtered.map((inc) => <IncidentCard key={inc.id} inc={inc} />)}
        </div>
      )}
    </div>
  );
}
